import configurationConstants from "../constants/Configuration";

/**
 * Cria um objeto representando as propriedades de uma turbina.
 *
 * @param {Object} properties - Dados de entrada para a criação das propriedades.
 * @param {number} properties.id - Identificador único da turbina.
 * @param {number} properties.monitoringElementId - ID do elemento de monitoramento associado à turbina.
 * @param {string} properties.manufacturer - Nome do fabricante da turbina.
 * @param {string} properties.model - Modelo da turbina.
 * @param {number} properties.turbineType - Tipo da turbina.
 * @param {number} properties.ratedPower - Potência nominal da turbina.
 * @param {number} properties.cutInWindSpeed - Velocidade mínima do vento para início da geração.
 * @param {number} properties.ratedWindSpeed - Velocidade do vento na qual a turbina atinge a potência nominal.
 * @param {number} properties.cutOutWindSpeed - Velocidade máxima do vento antes de a turbina ser desligada
 *  por segurança.
 * @param {number} properties.gridFrequency - Frequência da rede elétrica à qual a turbina está conectada.
 * @param {Object} properties.sCurve - Curva característica de potência da turbina.
 * @param {number} properties.generatorNumberOfPoles - Número de polos do gerador da turbina.
 * @returns {Object} Objeto com as propriedades normalizadas da turbina.
 */
export const TurbinePropertiesFactory = function(properties = {})
{
	return {
		id:                     properties.id,
		monitoringElementId:    properties.monitoringElementId,
		manufacturer:           properties.manufacturer ||           "",
		model:                  properties.model ||                  "",
		turbineType:            properties.turbineType ||            "",
		ratedPower:             properties.ratedPower ||             "",
		cutInWindSpeed:         properties.cutInWindSpeed ||         "",
		ratedWindSpeed:         properties.ratedWindSpeed ||         "",
		cutOutWindSpeed:        properties.cutOutWindSpeed ||        "",
		gridFrequency:          properties.gridFrequency ||          "",
		sCurve:                 properties.sCurve || null,
		generatorNumberOfPoles: properties.generatorNumberOfPoles || "",
	};
};

/**
 * Cria um objeto representando uma versão da turbina.
 *
 * @param {Object} turbineVersion - Dados de entrada da versão da turbina.
 * @param {number} turbineVersion.id - Identificador da versão.
 * @param {number} turbineVersion.turbinePropertiesId - ID das propriedades associadas à versão da turbina.
 * @param {string} turbineVersion.newEffectiveSince - Data de início da vigência da nova versão.
 * @param {string} turbineVersion.effectiveSince - Data de início da vigência da versão anterior.
 * @param {number} turbineVersion.userId - ID do usuário responsável pela criação da versão.
 * @param {string} turbineVersion.createdAt - Data de criação da versão.
 * @returns {Object} Objeto com os dados da versão da turbina.
 */
export const TurbineVersionFactory =  function(turbineVersion = {})
{
	return {
		id:                  turbineVersion.id,
		turbinePropertiesId: turbineVersion.turbinePropertiesId,
		effectiveSince:      turbineVersion.effectiveSince || "",
		newEffectiveSince:   turbineVersion.newEffectiveSince || "",
		userId:              turbineVersion.userId || 0,
		createdAt:           turbineVersion.createdAt || "",
	};
};

/**
 * Cria um objeto representando um rolamento de eixo.
 *
 * @param {Object} bearing - Dados do rolamento.
 * @param {number} bearing.id - Identificador do rolamento.
 * @param {number} bearing.shaftId - ID do eixo associado ao rolamento.
 * @param {string} bearing.bearingManufacturer - Fabricante do rolamento.
 * @param {string} bearing.model - Modelo do rolamento.
 * @param {string} bearing.name - Nome do rolamento.
 * @param {number} bearing.region - Região do rolamento na máquina.
 * @param {number} bearing.subRegion - Sub-região do rolamento.
 * @param {number} bearing.bpfo - Frequência de passagem externa dos elementos rolantes.
 * @param {number} bearing.bpfi - Frequência de passagem interna dos elementos rolantes.
 * @param {number} bearing.bsf - Frequência de modulação dos elementos rolantes.
 * @param {number} bearing.ftf - Frequência de rotação da gaiola do rolamento.
 * @returns {Object} Objeto com os dados do rolamento.
 */
export const ShaftBearingFactory = function(bearing = {})
{
	return {
		id:                  bearing.id,
		shaftId:             bearing.shaftId ?? 0,
		bearingManufacturer: bearing.bearingManufacturer || "",
		model:               bearing.model || "",
		name:                bearing.name || "",
		region:              bearing.region ?? "",
		subRegion:           bearing.subRegion ?? "",
		bpfo:                bearing.bpfo || "",
		bpfi:                bearing.bpfi || "",
		bsf:                 bearing.bsf || "",
		ftf:                 bearing.ftf || ""
	};
};

/**
 * Cria uma lista de objetos representando rolamentos de eixo.
 *
 * @param {Array<Object>} bearings - Lista de dados dos rolamentos.
 * @returns {Array<Object>} Lista de objetos de rolamentos normalizados.
 */
const ShaftBearingsFactory = function(bearings = [])
{
	return bearings.map(bearing =>
	{
		return {
			id:                  bearing.id,
			shaftId:             bearing.shaftId ?? 0,
			bearingManufacturer: bearing.bearingManufacturer || "",
			model:               bearing.model || "",
			name:                bearing.name || "",
			region:              bearing.region ?? "",
			subRegion:           bearing.subRegion ?? "",
			bpfo:                bearing.bpfo || "",
			bpfi:                bearing.bpfi || "",
			bsf:                 bearing.bsf || "",
			ftf:                 bearing.ftf || ""
		};
	});
};

/**
 * Cria um objeto representando um eixo da turbina.
 *
 * @param {Object} shaft - Dados do eixo.
 * @param {number} shaft.id - Identificador do eixo.
 * @param {number} shaft.turbineVersionId - ID da versão da turbina associada.
 * @param {string} shaft.name - Nome do eixo.
 * @param {number} shaft.rotorOrder - Ordem do rotor na cadeia cinemática.
 * @param {number} shaft.type - Tipo de eixo (ex.: rotor principal, intermediário).
 * @param {boolean} shaft.referenceForRotation - Define se este eixo é referência para rotação.
 * @param {Array<Object>} shaft.bearings - Lista de rolamentos associados ao eixo.
 * @returns {Object} Objeto representando o eixo da turbina.
 */
export const TurbineShaftsFactory = function(shaft = {})
{
	return {
		id:                   shaft.id ?? 0,
		turbineVersionId:     shaft.turbineVersionId,
		name:                 shaft.name || "",
		rotorOrder:           shaft.rotorOrder,
		type:                 shaft.type,
		referenceForRotation: shaft.referenceForRotation,
		bearings:             ShaftBearingsFactory(shaft.bearings) || [ ShaftBearingFactory() ],
	};
};

/**
 * Cria um objeto representando uma engrenagem.
 *
 * @param {Object} gears - Dados da engrenagem.
 * @param {number} gears.id - Identificador da engrenagem.
 * @param {number} gears.shaftId - ID do eixo ao qual a engrenagem está conectada.
 * @param {number} gears.gearboxStageId - ID do estágio da caixa de engrenagens.
 * @param {number} gears.numTeeth - Número de dentes da engrenagem.
 * @param {number} gears.type - Tipo de engrenagem (ex.: planetária, solar).
 * @returns {Object} Objeto com os dados da engrenagem.
 */
export const GearsFactory = function(gears = {})
{
	return {
		id:             gears.id,
		shaftId:        gears.shaftId ?? null,
		gearboxStageId: gears.gearboxStageId ?? null,
		numTeeth:       gears.numTeeth || "",
		type:           gears.type || ""
	};
};

/**
 * Cria um objeto representando a caixa de engrenagens (gearbox).
 *
 * @param {Object} gearbox - Dados da caixa de engrenagens.
 * @param {number} gearbox.id - Identificador da caixa.
 * @param {number} gearbox.turbineVersionId - ID da versão da turbina associada.
 * @param {string} gearbox.manufacturer - Fabricante da caixa de engrenagens.
 * @param {string} gearbox.name - Nome da caixa de engrenagens.
 * @param {number} gearbox.type - Tipo da caixa de engrenagens.
 * @param {number} gearbox.ratio - Relação de transmissão da caixa.
 * @returns {Object} Objeto com os dados da caixa de engrenagens.
 */
export const GearboxFactory = function(gearbox = {})
{
	return {
		id:               gearbox?.id,
		turbineVersionId: gearbox?.turbineVersionId,
		manufacturer:     gearbox?.manufacturer || "",
		name:             gearbox?.name || "",
		type:             gearbox?.type || null,
		ratio:            gearbox?.ratio || "",
	};
};

/**
 * Cria um objeto representando rotações de engrenagens planetárias.
 *
 * @param {Object} planetaryGearsRotation - Dados das rotações planetárias.
 * @param {number} planetaryGearsRotation.id - Identificador das rotações.
 * @param {number} planetaryGearsRotation.gearboxStageId - ID do estágio da caixa de engrenagens.
 * @param {number} planetaryGearsRotation.sunRotation - Rotação do sol (engrenagem central).
 * @param {number} planetaryGearsRotation.planetRotation - Rotação dos planetas.
 * @param {number} planetaryGearsRotation.cageRotation - Rotação da gaiola (suporte dos planetas).
 * @param {number} planetaryGearsRotation.planetsGearingOrder - Ordem de engrenamento dos planetas.
 * @returns {Object} Objeto com os dados das rotações planetárias.
 */
export const PlanetaryGearsRotationsFactory = function(planetaryGearsRotation = {})
{
	return {
		id:                  planetaryGearsRotation.id,
		gearboxStageId:      planetaryGearsRotation.gearboxStageId ?? 0,
		sunRotation:         planetaryGearsRotation.sunRotation ?? "",
		planetRotation:      planetaryGearsRotation.planetRotation ?? "",
		cageRotation:        planetaryGearsRotation.cageRotation ?? "",
		planetsGearingOrder: planetaryGearsRotation.planetsGearingOrder ?? ""
	};
};

/**
 * Cria um objeto representando um estágio da caixa de engrenagens.
 *
 * @param {Object} GearboxStagesModel - Dados do estágio da caixa.
 * @param {number} GearboxStagesModel.id - Identificador do estágio.
 * @param {number} GearboxStagesModel.gearboxModelId - ID da caixa de engrenagens associada.
 * @param {string} GearboxStagesModel.name - Nome do estágio.
 * @param {number} GearboxStagesModel.type - Tipo do estágio (planetário, paralelo, etc).
 * @param {string} GearboxStagesModel.stageSequence - Sequência do estágio na cadeia cinemática.
 * @param {Object} GearboxStagesModel.planetaryGearsRotation - Objeto com rotações planetárias associadas.
 * @returns {Object} Objeto representando o estágio da caixa de engrenagens.
 */
export const GearboxStagesFactory = function(GearboxStagesModel = {})
{
	return {
		id:                     GearboxStagesModel.id,
		gearboxModelId:         GearboxStagesModel.gearboxModelId,
		name:                   GearboxStagesModel.name || "",
		type:                   GearboxStagesModel.type || "",
		stageSequence:          GearboxStagesModel.stageSequence ?? null,
		planetaryGearsRotation: GearboxStagesModel.planetaryGearsRotation ?
			PlanetaryGearsRotationsFactory(GearboxStagesModel.planetaryGearsRotation) :
			null,
	};
};

/**
 * Cria um objeto representando as frequências de engrenamento (gear mesh).
 *
 * @param {Object} gearsMeshFrequencies - Dados das frequências de engrenamento.
 * @param {number} gearsMeshFrequencies.id - Identificador do conjunto de frequência.
 * @param {number} gearsMeshFrequencies.gearboxStageId - ID do estágio da caixa de engrenagens.
 * @param {number} gearsMeshFrequencies.shaftId - ID do eixo associado.
 * @param {number} gearsMeshFrequencies.gearMeshFrequency - Valor da frequência de engrenamento.
 * @returns {Object} Objeto com os dados da frequência de engrenamento.
 */
export const GearsMeshFrequenciesFactory = function(gearsMeshFrequencies = {})
{
	return {
		id:                gearsMeshFrequencies.id,
		gearboxStageId:    gearsMeshFrequencies.gearboxStageId || 0,
		shaftId:           gearsMeshFrequencies.shaftId || 0,
		gearMeshFrequency: gearsMeshFrequencies.gearMeshFrequency || "",
	};
};

/**
 * Cria uma configuração vazia contendo todas as entidades necessárias da turbina.
 *
 * @returns {Object} Objeto de configuração inicial com estruturas de turbina, caixa de engrenagens,
 *  eixos e engrenagens.
 */
export const CreateEmptyConfig = () =>
{
	return {
		turbineProperties:    TurbinePropertiesFactory(),
		turbineVersion:       TurbineVersionFactory(),
		shaftsModel:          [ TurbineShaftsFactory() ],
		gearbox:              GearboxFactory(),
		gearboxStages:        [ GearboxStagesFactory() ],
		gearsMeshFrequencies: [ GearsMeshFrequenciesFactory() ],
		gears:                [ GearsFactory() ],
	};
};